/**
 * Referral code capture for `?ref=CODE` links (e.g. underwritre.com/?ref=REPCODE).
 *
 * The Auth0 round trip drops query params: redirect_uri is window.location.origin,
 * so a prospect arriving on /?ref=REPCODE comes back from the login screen with a
 * bare origin and the code gone. We therefore stash it BEFORE any redirect and
 * read it back after the callback.
 *
 * sessionStorage (not localStorage) is deliberate: it survives the redirect in
 * the same tab, but doesn't outlive the browser session. A code shouldn't linger
 * for weeks and silently attribute an unrelated signup to the wrong rep.
 */

const STORAGE_KEY = 'uw_pending_referral_code';

/** Max length mirrors the Settings field so a junk URL can't stuff the store. */
const MAX_LENGTH = 32;

/**
 * Read `?ref=` off the current URL, persist it, and strip it from the address
 * bar so a later refresh or a shared link doesn't re-apply a stale code.
 * Safe to call on every app load; a no-op when there's no param.
 */
export function captureReferralFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('ref');
    if (!raw) return;

    const code = raw.trim().toUpperCase().slice(0, MAX_LENGTH);
    if (code) sessionStorage.setItem(STORAGE_KEY, code);

    params.delete('ref');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  } catch {
    // Private-browsing modes can throw on sessionStorage. A missing referral
    // code must never block the app from loading.
  }
}

/** The code captured from a `?ref=` link this session, if any. */
export function getPendingReferral(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Clear after it has been claimed, so it isn't re-offered on a later page. */
export function clearPendingReferral(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see captureReferralFromUrl */
  }
}
